import { memo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import {
  ENFORCE_MFA_ENABLE_MFA,
  ENFORCE_MFA_SETUP_LANDING,
  ENFORCE_MFA_VERIFY_EMAIL,
  OTP_LOGIN,
  RECOVERY_CODE_LOGIN,
} from '@hotjar/webapp/constants/routes/mfa';
import { RegistrationPage } from '@hotjar/webapp/features/Registration';
import { MfaEnforceSetupLandingPage } from '@hotjar/webapp/features/Mfa/MfaEnforceSetupLandingPage';
import { Heatmaps, RedirectMHRoutesToCCHeatmaps } from '@hotjar/webapp/features/Heatmaps';
import { CollectionsList } from '@hotjar/webapp/features/Collections/CollectionsList';
import { SsoSettingsPage } from '@hotjar/webapp/features/Settings/SsoSettingsPage';
import { VerifyEmailPage } from '@hotjar/webapp/features/EmailVerification';
import { MfaEnforceEnableMfaPage } from '@hotjar/webapp/features/Mfa/MfaEnforceEnableMfaPage/MfaEnforceEnableMfaPage';
import { ReferralsPage } from '@hotjar/webapp/features/Referrals/ReferralsPage';
import {
  BlockIfIncident,
  Checkout,
  InvoicesPage,
  PaymentDetailsPage,
  Subscriptions,
} from '@hotjar/subscription-management';
import { Login } from '@hotjar/webapp/features/Login/Login';
import { LoginOtpPage } from '@hotjar/webapp/features/Mfa/MfaLoginOtp';
import { LoginRecoveryCodePage } from '@hotjar/webapp/features/Mfa/MfaLoginRecoveryCode';
import { RecordingsPage } from '@hotjar/recordings';
import { AccountPage } from '@hotjar/webapp/features/Settings/Account';
import { CreateSitePage } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/Site/CreateSite/CreateSitePage';
import { AddOrganizationPage } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/AddOrganization/AddOrganizationPage';
import { SitesAndOrganizationPage } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/SitesAndOrganizationsPage';
import SiteSettingsPage from '@hotjar/webapp/features/Settings/SiteSettingsPage';
import { TeamListPage } from '@hotjar/webapp/features/Settings/Team/TeamList/TeamListPage';
import { TeamInvitePage } from '@hotjar/webapp/features/Settings/Team/TeamInvite/TeamInvitePage';
import { JoinTeam } from '@hotjar/webapp/features/JoinTeam';
import { NgApp } from '@hotjar/webapp/features/Angular/NgApp';
import { VisitorLookupPage } from '@hotjar/webapp/features/Settings/VisitorLookup/VisitorLookupPage';
import {
  CHECKOUT,
  DASHBOARD_QUANTZ,
  FUNNELS,
  HEATMAPS,
  HIGHLIGHTS,
  OVERVIEW,
  PERMISSION_REQUIRED,
  PLAYBACKS,
  PROFILE,
  SETTINGS,
  SITE,
  TRENDS,
  TEAM,
  REGISTER,
} from '@hotjar/webapp/constants/routes';
import { MH_PATHS } from '@hotjar/webapp/constants/routes/heatmaps-manual';
import { OnboardingPage } from '@hotjar/webapp/features/Activation/OnboardingPage';
import { Overview } from '@hotjar/webapp/features/Activation/SpaceOverview';
import { SsoSignInPage } from '@hotjar/webapp/features/Activation/SsoSignIn';
import { SetupWizard } from '@hotjar/webapp/features/Activation/SetupWizard';
import { PermissionRequiredPage } from '@hotjar/webapp/features/Activation/PermissionRequiredPage';
import { AsyncDashboardQuantzPage } from '@hotjar/webapp/containers/AsyncDashboardQuantzPage';
import { AsyncTrendsPage } from '@hotjar/webapp/containers/TrendsPage';
import { AsyncFunnelsPage } from '@hotjar/webapp/containers/FunnelsPage';
import { AsyncUserAttributesPage } from '@hotjar/webapp/containers/UserAttributesPage/AsyncUserAttributesPage';
import { SSO, USER_LOOKUP } from '@hotjar/webapp/constants/routes/settings';
import { PublicApi } from '@hotjar/auth';
import { useCurrentUser } from '@hotjar/user';

import {
  SSOAccessDenied,
  SubscriptionsAccessDenied,
  UserAttributesAccessDenied,
} from '@hotjar/webapp/features/Settings/AccessDenied';
import {
  FORGOT_PASSWORD,
  LOGIN,
  LOGOUT,
  RESET_PASSWORD,
  RESET_PASSWORD_LEGACY,
  SET_PASSWORD,
  SET_PASSWORD_LEGACY,
  SSO_SIGN_IN,
} from '@hotjar/webapp/constants/routes/auth';
import { JOIN_TEAM } from '@hotjar/webapp/constants/routes/onboarding';
import { NO_SITE_ACCESS } from '@hotjar/webapp/constants/routes/status';
import { isBillingV3Enabled } from '@hotjar/webapp/domains/billing/features';
import { useOwnedAccount } from '@hotjar/account';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import {
  MicrofrontendErrorBoundary,
  MicrofrontendPrototype,
} from '@hotjar/webapp/microfrontend-prototype';

import {
  AccountOwnerRoute,
  AuthenticatedRoute,
  ErrorBoundaryRoute,
  MobileReadyRoute,
  NoAppInterfaceRoute,
  NoSiteAccessRoute,
  OwnerOrAdminRoute,
  SiteAccessRequiredRoute,
  WithAppInterfaceRoute,
} from '@hotjar/webapp-routing';
import {
  ProfileDetails,
  SignInActivity,
  ProfileNotifications,
  ProfileLanguage,
  ProfileEmail,
  ProfileChangePassword,
} from '@hotjar/profile';

import { ApplicationBaseRoute } from '@hotjar/webapp/features/ApplicationBaseRoute/ApplicationBaseRoute';
import { Logout } from '@hotjar/webapp/features/Logout/Logout';
import { ResetPasswordPage } from '@hotjar/webapp/features/Password/ResetPasswordPage';
import { ForgotPasswordPage } from '@hotjar/webapp/features/Password/ForgotPasswordPage';
import { WithGettingStartedSideBar } from '@hotjar/webapp/features/Activation/GetStartedGuide';
import { useOptimizelyFeatureForUser } from '@hotjar/experimentation';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { FeedbackRoutes } from './routes/FeedbackRoutes';
import { IntegrationsRoutes } from './routes/IntegrationsRoutes';
import { SurveyRoutes } from './routes/SurveyRoutes';
import { SurveyTemplateRoutes } from './routes/SurveyTemplateRoutes';
import { WorkspaceToNonWorkspaceRoutes } from './routes/WorkspaceToNonWorkspaceRoutes';
import { EngageInsightsRoutes } from './routes/EngageInsightsRoutes';
import { FallbackRouteHandler } from './routes/FallbackRouteHandler';

const InternalAppRoutes = ({ currentSiteId }: { currentSiteId: string | null }): JSX.Element => {
  const { data: ownedAccount } = useOwnedAccount();
  const canAccessBillingV3Routes = isBillingV3Enabled(ownedAccount);
  const hasMicrofrontendPrototype = useFeatureFlag('microfrontend_prototype');
  const hasEngageInsights = useFeatureFlag('engage_insights');
  const hasEngageAccessTeam = usePermission('topspin-access-team');
  const hasEngageCreateTeam = usePermission('topspin-create-team');
  const { data: currentUser } = useCurrentUser();
  const showPublicApiKeys = useOptimizelyFeatureForUser('sea_public_api_keys');

  return (
    <Routes>
      <Route
        path="/"
        element={
          <MobileReadyRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="root-page">
                <ApplicationBaseRoute />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </MobileReadyRoute>
        }
      />

      {hasMicrofrontendPrototype && (
        <Route
          path="/microfrontend-prototype"
          element={
            <MicrofrontendErrorBoundary>
              <MicrofrontendPrototype />
            </MicrofrontendErrorBoundary>
          }
        />
      )}

      {/* Authenticated routes */}

      {/* Keep this around so we can ensure a redirect from the old WS routes to the up to date ones without WS */}
      {WorkspaceToNonWorkspaceRoutes()}

      <Route
        path={HIGHLIGHTS.LIST.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="workflow" featureName="highlight.list">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <CollectionsList />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      {/* Heatmap routes */}

      {/* Redirect all manual heatmap-related routes to CC heatmaps */}
      {MH_PATHS.map((path) => (
        <Route key={path} path={path} element={<RedirectMHRoutesToCCHeatmaps />} />
      ))}

      <Route
        path={HEATMAPS.LIST_CONTINUOUS_CAPTURE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="heatmaps" featureName="heatmaps">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <Heatmaps />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path={SSO.path}
        element={
          <AuthenticatedRoute>
            <OwnerOrAdminRoute fallbackUI={<SSOAccessDenied />}>
              <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="sso.settings">
                <SsoSettingsPage />
              </ErrorBoundaryRoute>
            </OwnerOrAdminRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={SSO_SIGN_IN.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="upsell" featureName="sso.signin">
            <SsoSignInPage />
          </ErrorBoundaryRoute>
        }
      />

      {FeedbackRoutes(currentSiteId)}

      {SurveyRoutes()}

      {SurveyTemplateRoutes(currentSiteId)}

      {/*
        this is a very niave approach to deny non-engage users access.
        We will move to a more sophisticated option when we want to start using permissions/entitlements as upsell oportunities
      */}
      {hasEngageAccessTeam && hasEngageCreateTeam && hasEngageInsights && EngageInsightsRoutes()}

      {IntegrationsRoutes()}

      <Route
        path={OVERVIEW.SETUP.path}
        element={
          <MobileReadyRoute>
            <SiteAccessRequiredRoute>
              <NoAppInterfaceRoute>
                <ErrorBoundaryRoute squadSentryTag="activation" featureName="overview.setup">
                  <SetupWizard />
                </ErrorBoundaryRoute>
              </NoAppInterfaceRoute>
            </SiteAccessRequiredRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={OVERVIEW.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="activation" featureName="overview">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <Overview />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path={DASHBOARD_QUANTZ.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="product-foundations" featureName="dashboard">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <AsyncDashboardQuantzPage />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path={TRENDS.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="product-foundations" featureName="trends">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <AsyncTrendsPage />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path={FUNNELS.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="product-foundations" featureName="funnels">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <AsyncFunnelsPage />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      {canAccessBillingV3Routes && (
        <>
          <Route
            path={CHECKOUT.BASE.path}
            element={
              <AuthenticatedRoute>
                <ErrorBoundaryRoute squadSentryTag="billing" featureName="settings.billing">
                  <AccountOwnerRoute fallbackUrl={SETTINGS.BILLING.path}>
                    <NoAppInterfaceRoute>
                      <BlockIfIncident page="checkout">
                        <Checkout />
                      </BlockIfIncident>
                    </NoAppInterfaceRoute>
                  </AccountOwnerRoute>
                </ErrorBoundaryRoute>
              </AuthenticatedRoute>
            }
          />

          <Route
            path={SETTINGS.SUBSCRIPTIONS.path}
            element={
              <AuthenticatedRoute>
                <ErrorBoundaryRoute squadSentryTag="billing" featureName="settings.subscriptions">
                  <AccountOwnerRoute fallbackUI={<SubscriptionsAccessDenied />}>
                    <BlockIfIncident page="subscriptions">
                      <Subscriptions />
                    </BlockIfIncident>
                  </AccountOwnerRoute>
                </ErrorBoundaryRoute>
              </AuthenticatedRoute>
            }
          />

          <Route
            path={SETTINGS.PAYMENT_DETAILS.path}
            element={
              <AuthenticatedRoute>
                <ErrorBoundaryRoute squadSentryTag="billing" featureName="settings.paymentdetails">
                  <AccountOwnerRoute fallbackUI={<SubscriptionsAccessDenied />}>
                    <BlockIfIncident page="payment_details">
                      <PaymentDetailsPage />
                    </BlockIfIncident>
                  </AccountOwnerRoute>
                </ErrorBoundaryRoute>
              </AuthenticatedRoute>
            }
          />

          <Route
            path={SETTINGS.INVOICES.path}
            element={
              <AuthenticatedRoute>
                <ErrorBoundaryRoute squadSentryTag="billing" featureName="settings.paymentdetails">
                  <AccountOwnerRoute fallbackUI={<SubscriptionsAccessDenied />}>
                    <BlockIfIncident page="invoices">
                      <InvoicesPage />
                    </BlockIfIncident>
                  </AccountOwnerRoute>
                </ErrorBoundaryRoute>
              </AuthenticatedRoute>
            }
          />
        </>
      )}

      <Route
        path={SETTINGS.USER_ATTRIBUTES.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute
              squadSentryTag="product-foundations"
              featureName="settings.userattributes"
            >
              <OwnerOrAdminRoute fallbackUI={<UserAttributesAccessDenied />}>
                <AsyncUserAttributesPage />
              </OwnerOrAdminRoute>
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={SETTINGS.ACCOUNT.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="settings.account">
              <AccountPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={TEAM.LIST.path}
        element={
          <AuthenticatedRoute>
            <TeamListPage />
          </AuthenticatedRoute>
        }
      />

      <Route
        path={TEAM.INVITE.path}
        element={
          <AuthenticatedRoute>
            <TeamInvitePage />
          </AuthenticatedRoute>
        }
      />

      <Route
        path={TEAM.INVITE_DEFAULT.path}
        element={
          <AuthenticatedRoute>
            <TeamInvitePage />
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.REFERRALS.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="acquisition" featureName="referrals">
              <ReferralsPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      {/*
        @TODO
        At the moment the redirect is handled by the RecordingsPage component.
        We will replace the legacy logic and leverage the Navigation component
        provided by react-route to handle redirects.

        https://hotjar.atlassian.net/browse/RECS-993
      */}
      <Route
        path={PLAYBACKS.LIST.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="recordings" featureName="recordings.list">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <RecordingsPage />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path={PLAYBACKS.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="recordings" featureName="recordings.list">
              <WithAppInterfaceRoute>
                <WithGettingStartedSideBar>
                  <RecordingsPage />
                </WithGettingStartedSideBar>
              </WithAppInterfaceRoute>
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route
        path="/onboarding"
        element={
          <MobileReadyRoute>
            <AuthenticatedRoute>
              <NoAppInterfaceRoute>
                <ErrorBoundaryRoute squadSentryTag="activation" featureName="onboarding">
                  <OnboardingPage />
                </ErrorBoundaryRoute>
              </NoAppInterfaceRoute>
            </AuthenticatedRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={REGISTER.BASE.path}
        element={
          <MobileReadyRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="register">
                <RegistrationPage />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={LOGIN.path}
        element={
          <MobileReadyRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
                <Login />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={LOGOUT.path}
        element={
          <MobileReadyRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
                <Logout />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={OTP_LOGIN.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login.otp">
            <LoginOtpPage />
          </ErrorBoundaryRoute>
        }
      />
      <Route
        path={RECOVERY_CODE_LOGIN.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="recovery.code">
            <LoginRecoveryCodePage />
          </ErrorBoundaryRoute>
        }
      />
      <Route
        path={ENFORCE_MFA_SETUP_LANDING.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="mfa.setup">
            <MfaEnforceSetupLandingPage />
          </ErrorBoundaryRoute>
        }
      />
      <Route
        path={ENFORCE_MFA_VERIFY_EMAIL.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="mfa.verify.email">
            <VerifyEmailPage backUrl={ENFORCE_MFA_SETUP_LANDING.path} />
          </ErrorBoundaryRoute>
        }
      />

      <Route
        path={ENFORCE_MFA_ENABLE_MFA.path}
        element={
          <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="mfa.enable">
            <MfaEnforceEnableMfaPage backUrl={ENFORCE_MFA_SETUP_LANDING.path} />
          </ErrorBoundaryRoute>
        }
      />

      <Route
        path={SITE.CREATE.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="site.create">
              <CreateSitePage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={SITE.CREATE_ORGANIZATION.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="organization.create">
              <AddOrganizationPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={SITE.LIST.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="site.list">
              <SitesAndOrganizationPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />
      <Route
        path={SITE.SETTINGS.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="site.settings">
              <SiteSettingsPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      {showPublicApiKeys && (
        <Route path={SETTINGS.PUBLIC_API.path} element={<PublicApi userId={currentUser?.id} />} />
      )}

      <Route
        path={PERMISSION_REQUIRED.BASE.path}
        element={
          <SiteAccessRequiredRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="permission.required.page">
                <PermissionRequiredPage />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </SiteAccessRequiredRoute>
        }
      />

      <Route path={PROFILE.BASE.path} element={<Navigate to={PROFILE.DETAILS.path} replace />} />

      <Route
        path={PROFILE.DETAILS.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="profile.details">
              <ProfileDetails />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.LANGUAGE.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="tame" featureName="profile.language">
              <ProfileLanguage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.EMAIL.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="profile.email">
              <ProfileEmail />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.NOTIFICATIONS.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="profile.notifications">
              <ProfileNotifications />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.PASSWORD.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="profile.password">
              <ProfileChangePassword />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={PROFILE.SIGN_IN_ACTIVITY.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="profile.details">
              <SignInActivity />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={USER_LOOKUP.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="settings.userlookup">
              <VisitorLookupPage />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      <Route
        path={RESET_PASSWORD.path}
        element={
          <NoAppInterfaceRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
              <ResetPasswordPage version="reset-password" />
            </ErrorBoundaryRoute>
          </NoAppInterfaceRoute>
        }
      />

      <Route
        path={RESET_PASSWORD_LEGACY.path}
        element={
          <NoAppInterfaceRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
              <ResetPasswordPage version="reset-password" />
            </ErrorBoundaryRoute>
          </NoAppInterfaceRoute>
        }
      />

      <Route
        path={SET_PASSWORD.path}
        element={
          <NoAppInterfaceRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
              <ResetPasswordPage version="set-password" />
            </ErrorBoundaryRoute>
          </NoAppInterfaceRoute>
        }
      />

      <Route
        path={SET_PASSWORD_LEGACY.path}
        element={
          <NoAppInterfaceRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
              <ResetPasswordPage version="set-password" />
            </ErrorBoundaryRoute>
          </NoAppInterfaceRoute>
        }
      />

      <Route
        path={FORGOT_PASSWORD.path}
        element={
          <NoAppInterfaceRoute>
            <ErrorBoundaryRoute squadSentryTag="security-and-auth" featureName="login">
              <ForgotPasswordPage />
            </ErrorBoundaryRoute>
          </NoAppInterfaceRoute>
        }
      />

      <Route
        path={JOIN_TEAM.path}
        element={
          <MobileReadyRoute>
            <NoAppInterfaceRoute>
              <ErrorBoundaryRoute squadSentryTag="activation" featureName="jointeam">
                <JoinTeam />
              </ErrorBoundaryRoute>
            </NoAppInterfaceRoute>
          </MobileReadyRoute>
        }
      />

      <Route
        path={NO_SITE_ACCESS.path}
        element={
          <AuthenticatedRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="nositeaccess">
              <NoSiteAccessRoute />
            </ErrorBoundaryRoute>
          </AuthenticatedRoute>
        }
      />

      {/* Validate site access and fallback to angular app for unmatched /sites/ routes */}
      <Route
        path="/sites/:siteId/*"
        element={
          <SiteAccessRequiredRoute>
            <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="site.angular.part">
              <NgApp />
            </ErrorBoundaryRoute>
          </SiteAccessRequiredRoute>
        }
      />

      {/* Fallback to angular app for any remaining unmatched routes */}
      <Route
        path="*"
        element={
          <ErrorBoundaryRoute squadSentryTag="hotjar" featureName="angular.part">
            <WithAppInterfaceRoute>
              <FallbackRouteHandler />
            </WithAppInterfaceRoute>
          </ErrorBoundaryRoute>
        }
      />
    </Routes>
  );
};

export const AppRoutes = memo(InternalAppRoutes);
